import { useEffect, useState } from 'react';
import { FormHelperText, Grid, InputAdornment, InputLabel, OutlinedInput } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { FormDrawer, FormDrawerGroup, SwitchField } from '../../../../components/form-drawer';
import { RichTextEditor } from '../../components/rich-text-editor';
import { DocumentAttachments } from '../../components/document-attachments';
import { resolveDateValue, toDateInputValue } from '../../../../utils/date';
import { parseNumberInput } from '../../../../utils/format';
import { ILookupResponse } from '../../../../model/apm/base/lookup-response';
import { IPortalDocumentResponse } from '../../../../model/apm/document/portal-document-response';
import { IPurchaseContractAddEditRequest } from '../../../../model/apm/purchase-contract/request/purchase-contract-add-edit-request';
import { IPurchaseContractResponse } from '../../../../model/apm/purchase-contract/response/purchase-contract-response';

interface PurchaseContractDrawerProps {
    open: boolean;
    initial?: IPurchaseContractResponse;
    files: IPortalDocumentResponse[];
    documentTypes: ILookupResponse[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: IPurchaseContractAddEditRequest) => void;
    onUploadFile: (documentTypeId: number, file: File, onProgress: (percent: number) => void) => Promise<void>;
    onDeleteFile: (file: IPortalDocumentResponse) => void;
}

type AmountField = 'amountPaid' | 'balance' | 'blockedAmount';

const AMOUNT_FIELDS: { key: AmountField; label: string }[] = [
    { key: 'amountPaid', label: 'Valor pago' },
    { key: 'balance', label: 'Saldo' },
    { key: 'blockedAmount', label: 'Valor bloqueado' },
];

const emptyValue: IPurchaseContractAddEditRequest = { isCovid19: false };

export function PurchaseContractDrawer({ open, initial, files, documentTypes, isSaving, onClose, onSave, onUploadFile, onDeleteFile }: PurchaseContractDrawerProps) {
    const [form, setForm] = useState<IPurchaseContractAddEditRequest>(emptyValue);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const amountsInvalid = AMOUNT_FIELDS.some(({ key }) => (form[key] ?? 0) < 0);
    const errors = {
        contractedCompanyName: showValidationErrors && !form.contractedCompanyName?.trim(),
        contractedCompanyDocument: showValidationErrors && !form.contractedCompanyDocument?.trim(),
        amounts: showValidationErrors && amountsInvalid,
    };

    useEffect(() => {
        if (!open) return;
        setForm(initial
            ? {
                contractedCompanyName: initial.contractedCompanyName,
                contractedCompanyDocument: initial.contractedCompanyDocument,
                processNumber: initial.processNumber,
                contractDeadline: toDateInputValue(initial.contractDeadline),
                description: initial.description,
                deliveryLocation: initial.deliveryLocation,
                amountPaid: initial.amountPaid,
                balance: initial.balance,
                blockedAmount: initial.blockedAmount,
                isCovid19: initial.isCovid19,
            }
            : { ...emptyValue });
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<IPurchaseContractAddEditRequest>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (!form.contractedCompanyName?.trim() || !form.contractedCompanyDocument?.trim() || amountsInvalid) {
            setShowValidationErrors(true);
            return;
        }
        onSave({
            ...form,
            contractedCompanyName: form.contractedCompanyName.trim(),
            contractedCompanyDocument: form.contractedCompanyDocument.trim(),
            processNumber: form.processNumber?.trim() || undefined,
            deliveryLocation: form.deliveryLocation?.trim() || undefined,
            contractDeadline: resolveDateValue(form.contractDeadline, initial?.contractDeadline),
            description: form.description || undefined,
        });
    }

    return (
        <FormDrawer
            open={open}
            icon={<DescriptionIcon />}
            title={isEdit ? 'Editar contrato' : 'Novo contrato'}
            subtitle={isEdit ? initial?.contractedCompanyName : 'Salve o contrato para anexar os documentos'}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar contrato'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Contratado">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                        <InputLabel required>Nome / razão social</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoFocus
                            autoComplete="off"
                            inputProps={{ maxLength: 250 }}
                            value={form.contractedCompanyName || ''}
                            onChange={(e) => update({ contractedCompanyName: e.target.value })}
                            error={errors.contractedCompanyName}
                        />
                        {errors.contractedCompanyName && <FormHelperText error>Contratado é obrigatório</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel required>CPF/CNPJ</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoComplete="off"
                            inputProps={{ maxLength: 30 }}
                            value={form.contractedCompanyDocument || ''}
                            onChange={(e) => update({ contractedCompanyDocument: e.target.value })}
                            error={errors.contractedCompanyDocument}
                        />
                        {errors.contractedCompanyDocument && <FormHelperText error>CPF/CNPJ é obrigatório</FormHelperText>}
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Contrato">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Número do processo</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoComplete="off"
                            inputProps={{ maxLength: 250 }}
                            value={form.processNumber || ''}
                            onChange={(e) => update({ processNumber: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Prazo do contrato</InputLabel>
                        <OutlinedInput fullWidth type="date" value={form.contractDeadline || ''} onChange={(e) => update({ contractDeadline: e.target.value || undefined })} />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel>Local de entrega</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoComplete="off"
                            inputProps={{ maxLength: 250 }}
                            value={form.deliveryLocation || ''}
                            onChange={(e) => update({ deliveryLocation: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel>Discriminação do objeto</InputLabel>
                        <RichTextEditor value={form.description} onChange={(description) => update({ description })} minHeight={140} />
                    </Grid>
                    <Grid item xs={12}>
                        <SwitchField
                            label="Contratação para enfrentamento da COVID-19"
                            description="Exibe o contrato também na relação de aquisições da COVID-19 no portal."
                            checked={form.isCovid19}
                            onChange={(isCovid19) => update({ isCovid19 })}
                        />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Valores">
                <Grid container spacing={2}>
                    {AMOUNT_FIELDS.map(({ key, label }) => (
                        <Grid item xs={12} sm={4} key={key}>
                            <InputLabel>{label}</InputLabel>
                            <OutlinedInput
                                fullWidth
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                                value={form[key] ?? ''}
                                onChange={(e) => update({ [key]: parseNumberInput(e.target.value) })}
                                error={errors.amounts && (form[key] ?? 0) < 0}
                            />
                        </Grid>
                    ))}
                    {errors.amounts && (
                        <Grid item xs={12}>
                            <FormHelperText error>Os valores pago, saldo e bloqueado não podem ser negativos</FormHelperText>
                        </Grid>
                    )}
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Documentos" description="Contrato, aditivos e demais arquivos publicados no portal. Os anexos são salvos na hora.">
                <DocumentAttachments
                    id="purchase-contract-file"
                    files={files}
                    documentTypes={documentTypes}
                    disabledMessage={isEdit ? undefined : 'Salve o contrato para anexar documentos. O formulário continua aberto após salvar.'}
                    onUpload={onUploadFile}
                    onDelete={onDeleteFile}
                />
            </FormDrawerGroup>
        </FormDrawer>
    );
}
